import { IntegrationEnhanced, useOrgIntegrations } from "@/api/integrations";
import { EntitySelector } from "@/components/EntitySelector";
import { EntitySelectionProvider } from "@/components/EntitySelector/dnd/useEntitySelection";
import {
  RailRowSkeleton,
  useRowReservation,
} from "@/components/rail-kit/RailReveal";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { hasPlatformEntitySelector } from "@/constants/integration-platforms";
import {
  VIEWPORT_POPOVER_COLLISION_PADDING,
  getViewportDocumentCollisionBoundary,
} from "@/constants/viewportPopoverCollision";
import { CANVAS_ADD_CONTEXT_TRIGGER_SELECTOR } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/canvas/canvasRailAddContextEvents";
import { WorkspaceCanvasDropdownCascade } from "@/object-actions/renderers/WorkspaceCanvasPickerSubmenu";
import { WorkspaceFileDropdownCascade } from "@/object-actions/renderers/WorkspaceFilePickerSubmenu";
import { CANVAS_FILE_UPLOAD_ACCEPT } from "@/pages/canvas/constants/canvasFileUpload.constants";
import type { QuickAddCursorState } from "@/pages/canvas/context/CanvasUiContext";
import type { CanvasInsertMenuActions } from "@/pages/canvas/hooks/useCanvasInsertMenuActions";
import type { FileRecord, IntegrationConnection } from "@cohesive/api-client";
import { type Entity, IntegrationIcon } from "@cohesive/ui-components";
import {
  ArrowLeft,
  ClipboardPaste,
  FileText,
  FolderUp,
  Link2,
  MessageCircle,
  MessageSquare,
  Mic,
  Plus,
  Sparkles,
  StickyNote,
  Upload,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { CANVAS_KEYS } from "../../../canvas-controls.config";
import { CanvasAddLinkPopover } from "../../CanvasAddLinkPopover";
import { CanvasWidgetsPopover } from "../../CanvasWidgetsPopover";
import { CanvasToolbarDropdown } from "./CanvasToolbarDropdown";
import { CANVAS_TOOLBAR_MENU_MIN_WIDTH } from "./canvasToolbarLayout.constants";
import { useCanvasToolbarLayoutOptional } from "./CanvasToolbarLayoutContext";
import { canvasToolbarDropdownContentClassName } from "./canvasToolbarStyles";

export type { CanvasInsertMenuActions } from "@/pages/canvas/hooks/useCanvasInsertMenuActions";

const INSERT_MENU_COMPACT_WIDTH = CANVAS_TOOLBAR_MENU_MIN_WIDTH;
const INSERT_MENU_WIDGET_WIDTH = 480;
const INSERT_MENU_LINK_WIDTH = 360;
export const INSERT_MENU_INTEGRATION_WIDTH = 680;
export const INSERT_MENU_INTEGRATION_HEIGHT = 520;

type MenuView =
  | { kind: "menu" }
  | { kind: "integration"; integration: IntegrationEnhanced };

function useElementAnchorRect(
  active: boolean,
  selector: string,
): DOMRect | null {
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!active) {
      setRect(null);
      return;
    }

    const update = () => {
      const el = document.querySelector(selector);
      setRect(el?.getBoundingClientRect() ?? null);
    };

    update();
    const el = document.querySelector(selector);
    if (!el) return;

    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);

    return () => {
      ro.disconnect();
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [active, selector]);

  return rect;
}

type CanvasInsertMenuBaseProps = CanvasInsertMenuActions & {
  onIntegrationEntitySelect?: (entity: Entity) => void;
  ingestContextMenuFiles?: (files: File[]) => Promise<void>;
  /** When true, upload opens an internal file input (context menu). */
  useInternalFileInput?: boolean;
  /** Hide the connected-integrations section — the primary toolbar's
   * Connectors trigger already carries those entries. */
  hideIntegrations?: boolean;
};

type CanvasInsertMenuToolbarProps = CanvasInsertMenuBaseProps & {
  variant: "toolbar";
  tooltipSide?: "top" | "right" | "bottom" | "left";
  onOpen?: () => void;
  isWidgetsMenuOpen?: boolean;
  isLinkMenuOpen?: boolean;
  onWidgetsMenuOpenChange?: (open: boolean) => void;
  onLinkMenuOpenChange?: (open: boolean) => void;
};

type CanvasInsertMenuAnchoredProps = CanvasInsertMenuBaseProps & {
  variant: "anchored";
  open: boolean;
  onOpenChange: (open: boolean) => void;
  anchor: {
    cursorPosition: QuickAddCursorState;
    anchorToRailTrigger?: boolean;
  };
};

export type CanvasInsertMenuProps =
  | CanvasInsertMenuToolbarProps
  | CanvasInsertMenuAnchoredProps;

export function CanvasInsertMenuExpandedHeader({
  icon,
  label,
  onBack,
}: {
  icon: React.ReactNode;
  label: string;
  onBack: () => void;
}) {
  return (
    <div className="flex shrink-0 items-center gap-2 border-b border-n-10 px-3 py-2.5">
      <Button
        variant="ghost"
        size="sm"
        onClick={onBack}
        className="h-6 gap-1 px-1.5"
      >
        <ArrowLeft className="size-3.5" />
        <span className="text-xs">Back</span>
      </Button>
      <span className="text-xs text-n-50">/</span>
      <div className="flex items-center gap-1.5">
        {icon}
        <span className="text-sm font-medium">{label}</span>
      </div>
    </div>
  );
}

function CanvasInsertMenuItems({
  onOpenWidgets,
  onOpenLink,
  onOpenIntegration,
  connectedIntegrations,
  integrationsPending,
  onNewNote,
  onNewDocument,
  onUploadFile,
  onUploadFolder,
  onAddFromLibrary,
  onAddCanvas,
  preventCloseOnUploadSelect,
  onStartQuickChat,
  onStartQuickVoice,
  onStartComment,
  onPaste,
}: {
  onOpenWidgets: () => void;
  onOpenLink: () => void;
  onOpenIntegration: (integration: IntegrationEnhanced) => void;
  connectedIntegrations: IntegrationEnhanced[];
  /** Integrations are still loading — hold their rows' height so the menu
   *  doesn't grow (and Radix doesn't reposition it) when they land. */
  integrationsPending?: boolean;
  onNewNote: () => void;
  onNewDocument: () => void;
  onUploadFile: () => void;
  onUploadFolder: () => void;
  onAddFromLibrary?: {
    workspaceId: string;
    onPick: (record: FileRecord, at?: { x: number; y: number }) => void;
  };
  onAddCanvas?: CanvasInsertMenuActions["onAddCanvas"];
  /** Keep menu mounted while the OS file picker is open (context menu upload). */
  preventCloseOnUploadSelect?: boolean;
  onStartQuickChat?: () => void;
  onStartQuickVoice?: () => void;
  onStartComment?: () => void;
  onPaste?: () => void;
}) {
  // Same reservation policy the rail uses: hold the integrations' height from
  // the first frame (learned from the last time this menu was open), and only
  // show pulsing placeholders if the fetch is genuinely slow.
  const {
    reservedRows: reservedIntegrationRows,
    skeletonVisible: integrationSkeletonVisible,
  } = useRowReservation({
    active: true,
    pending: !!integrationsPending,
  });

  return (
    <>
      {(onStartQuickChat || onStartQuickVoice || onStartComment) && (
        <>
          {onStartComment && (
            <DropdownMenuItem onSelect={onStartComment}>
              <MessageCircle className="size-4" />
              Comment
              <DropdownMenuShortcut>
                {CANVAS_KEYS.comment.toUpperCase()}
              </DropdownMenuShortcut>
            </DropdownMenuItem>
          )}
          {onStartQuickChat && (
            <DropdownMenuItem onSelect={onStartQuickChat}>
              <MessageSquare className="size-4" />
              Quick Chat
              <DropdownMenuShortcut>
                {CANVAS_KEYS.quickChat.toUpperCase()}
              </DropdownMenuShortcut>
            </DropdownMenuItem>
          )}
          {onStartQuickVoice && (
            <DropdownMenuItem onSelect={onStartQuickVoice}>
              <Mic className="size-4" />
              Voice Input
              <DropdownMenuShortcut>
                {CANVAS_KEYS.voiceInput.toUpperCase()}
              </DropdownMenuShortcut>
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
        </>
      )}

      {onPaste && (
        <>
          <DropdownMenuItem onSelect={onPaste}>
            <ClipboardPaste className="size-4" />
            Paste
            <DropdownMenuShortcut>⌘V</DropdownMenuShortcut>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
        </>
      )}

      <DropdownMenuItem
        onSelect={(event) => {
          event.preventDefault();
          onOpenWidgets();
        }}
      >
        <Sparkles className="size-4" />
        Add Widget
        <DropdownMenuShortcut>
          {CANVAS_KEYS.addWidgets.toUpperCase()}
        </DropdownMenuShortcut>
      </DropdownMenuItem>

      <DropdownMenuSeparator />

      <DropdownMenuItem onSelect={onNewNote}>
        <StickyNote className="size-4" />
        New Note
        <DropdownMenuShortcut>
          {CANVAS_KEYS.addNote.toUpperCase()}
        </DropdownMenuShortcut>
      </DropdownMenuItem>
      <DropdownMenuItem onSelect={onNewDocument}>
        <FileText className="size-4" />
        New Document
        <DropdownMenuShortcut>
          {CANVAS_KEYS.addDoc.toUpperCase()}
        </DropdownMenuShortcut>
      </DropdownMenuItem>
      <DropdownMenuItem
        onSelect={(event) => {
          event.preventDefault();
          onOpenLink();
        }}
      >
        <Link2 className="size-4" />
        New Link
        <DropdownMenuShortcut>
          {CANVAS_KEYS.addLink.toUpperCase()}
        </DropdownMenuShortcut>
      </DropdownMenuItem>
      {/* No picker offers both — `webkitdirectory` is fixed when the input
          opens — so the choice nests. SubContent renders inside this panel's
          layer, so the file leaf's `preventDefault` still holds the menu open
          across the OS dialog the way the context-menu upload needs. */}
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <Upload className="size-4" />
          Upload
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          <DropdownMenuItem
            onSelect={(event) => {
              if (preventCloseOnUploadSelect) {
                event.preventDefault();
              }
              onUploadFile();
            }}
          >
            <Upload className="size-4" />
            File
            <DropdownMenuShortcut>
              {CANVAS_KEYS.uploadFile.toUpperCase()}
            </DropdownMenuShortcut>
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={onUploadFolder}>
            <FolderUp className="size-4" />
            Folder
          </DropdownMenuItem>
        </DropdownMenuSubContent>
      </DropdownMenuSub>
      {onAddFromLibrary && (
        // Nested pop: drill the workspace tree in place; clicking a file mints
        // a reference on this canvas — same model as Move to / Copy to.
        <WorkspaceFileDropdownCascade
          workspaceId={onAddFromLibrary.workspaceId}
          onPick={onAddFromLibrary.onPick}
        />
      )}
      {onAddCanvas && (
        // Same nested pop for canvases; the pick lands as a link card.
        <WorkspaceCanvasDropdownCascade
          workspaceId={onAddCanvas.workspaceId}
          currentCanvasId={onAddCanvas.currentCanvasId}
          onPick={onAddCanvas.onPick}
        />
      )}

      {integrationsPending && reservedIntegrationRows > 0 && (
        <>
          <DropdownMenuSeparator />
          {Array.from({ length: reservedIntegrationRows }, (_, i) => (
            <RailRowSkeleton
              key={i}
              size="menu"
              visible={integrationSkeletonVisible}
            />
          ))}
        </>
      )}

      {!integrationsPending && connectedIntegrations.length > 0 && (
        <>
          <DropdownMenuSeparator />
          {connectedIntegrations.map((integration) => (
            <DropdownMenuItem
              key={integration.id}
              onSelect={(event) => {
                event.preventDefault();
                onOpenIntegration(integration);
              }}
            >
              <IntegrationIcon
                platform={integration.platform}
                name={integration.name}
                imageURL={integration.metadata?.image_url}
                className="size-4 rounded"
              />
              {integration.name}
            </DropdownMenuItem>
          ))}
        </>
      )}
    </>
  );
}

function useInsertMenuState(
  props: CanvasInsertMenuProps,
  externalOpen?: boolean,
  onExternalOpenChange?: (open: boolean) => void,
) {
  const [internalInsertOpen, setInternalInsertOpen] = useState(false);
  const [internalWidgetsOpen, setInternalWidgetsOpen] = useState(false);
  const [internalLinkOpen, setInternalLinkOpen] = useState(false);
  const [view, setView] = useState<MenuView>({ kind: "menu" });

  const isToolbar = props.variant === "toolbar";
  const widgetsMenuOpen =
    (isToolbar ? props.isWidgetsMenuOpen : undefined) ?? internalWidgetsOpen;
  const linkMenuOpen =
    (isToolbar ? props.isLinkMenuOpen : undefined) ?? internalLinkOpen;
  const insertMenuOpen = isToolbar
    ? internalInsertOpen
    : (externalOpen ?? false);

  const setWidgetsMenuOpen = (open: boolean) => {
    setInternalWidgetsOpen(open);
    if (isToolbar) props.onWidgetsMenuOpenChange?.(open);
  };

  const setLinkMenuOpen = (open: boolean) => {
    setInternalLinkOpen(open);
    if (isToolbar) props.onLinkMenuOpenChange?.(open);
  };

  const closeReplacementViews = () => {
    setWidgetsMenuOpen(false);
    setLinkMenuOpen(false);
    setView({ kind: "menu" });
  };

  useEffect(() => {
    if (!isToolbar && externalOpen === false) {
      closeReplacementViews();
    }
  }, [isToolbar, externalOpen]);

  const handleOpenChange = (open: boolean) => {
    if (isToolbar) {
      setInternalInsertOpen(open);
    } else {
      onExternalOpenChange?.(open);
    }
    if (!open) {
      closeReplacementViews();
    }
  };

  const replacementViewOpen =
    widgetsMenuOpen || linkMenuOpen || view.kind === "integration";

  const minWidth =
    view.kind === "integration"
      ? INSERT_MENU_INTEGRATION_WIDTH
      : widgetsMenuOpen
        ? INSERT_MENU_WIDGET_WIDTH
        : linkMenuOpen
          ? INSERT_MENU_LINK_WIDTH
          : INSERT_MENU_COMPACT_WIDTH;

  return {
    view,
    setView,
    insertMenuOpen,
    widgetsMenuOpen,
    linkMenuOpen,
    replacementViewOpen,
    minWidth,
    setWidgetsMenuOpen,
    setLinkMenuOpen,
    closeReplacementViews,
    handleOpenChange,
  };
}

function buildConnectionForSelector(
  integration: IntegrationEnhanced,
): IntegrationConnection | null {
  if (!integration.integration_connection_id) {
    return null;
  }
  return {
    id: integration.integration_connection_id,
    integration_id: integration.id,
    integration_name: integration.name,
    integration_platform: integration.platform,
    integration_image_url: integration.metadata?.image_url,
    integration_provider: integration.provider,
    integration_provider_id: integration.provider_id,
    organization_id: "",
    user_id: "",
    status: "active",
    beta: false,
    config: integration.integration_connection_config || {},
    instructions: "",
    created_at: integration.created_at,
    updated_at: integration.updated_at,
  };
}

function CanvasInsertMenuBody({
  props,
  menuState,
  onCloseMenu,
  pickingFileRef,
}: {
  props: CanvasInsertMenuBaseProps;
  menuState: ReturnType<typeof useInsertMenuState>;
  onCloseMenu: () => void;
  pickingFileRef: React.RefObject<boolean>;
}) {
  const { data: integrations = [], isPending: integrationsPending } =
    useOrgIntegrations();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const connectedIntegrations = props.hideIntegrations
    ? []
    : integrations
        .filter(
          (i) =>
            i.connected &&
            i.integration_connection_id &&
            hasPlatformEntitySelector(i.platform),
        )
        .sort((a, b) => a.name.localeCompare(b.name));

  const {
    view,
    setView,
    widgetsMenuOpen,
    linkMenuOpen,
    replacementViewOpen,
    setWidgetsMenuOpen,
    setLinkMenuOpen,
    closeReplacementViews,
  } = menuState;

  const openFilePicker = useCallback(() => {
    pickingFileRef.current = true;
    const clear = () => {
      pickingFileRef.current = false;
      window.removeEventListener("focus", clear);
    };
    window.addEventListener("focus", clear);
    fileInputRef.current?.click();
  }, []);

  const handleUploadSelect = useCallback(() => {
    if (props.useInternalFileInput) {
      openFilePicker();
      return;
    }
    props.onUploadFile();
  }, [props, openFilePicker]);

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    pickingFileRef.current = false;
    const files = event.target.files;
    if (!files || files.length === 0) return;
    // Copy before clearing — `input.files` is a live FileList that empties
    // when value is reset (same pattern as useCanvasToolbarFileUpload).
    const fileList = Array.from(files);
    // Reset the input synchronously so re-picking the same file fires change.
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (props.ingestContextMenuFiles) {
      await props.ingestContextMenuFiles(fileList);
    }
    onCloseMenu();
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      if (replacementViewOpen) {
        closeReplacementViews();
        return;
      }
      onCloseMenu();
    };
    window.addEventListener("keydown", handler, { capture: true });
    return () =>
      window.removeEventListener("keydown", handler, { capture: true });
  }, [replacementViewOpen, closeReplacementViews, onCloseMenu]);

  const handleIntegrationSelect = (entity: Entity) => {
    if (props.onIntegrationEntitySelect) {
      props.onIntegrationEntitySelect(entity);
    } else {
      props.onSelectEntity(entity, { x: 0, y: 0 });
    }
    onCloseMenu();
  };

  if (widgetsMenuOpen) {
    return (
      <CanvasWidgetsPopover
        onAddWidget={(preset) => {
          props.onAddWidget(preset);
          onCloseMenu();
        }}
      />
    );
  }

  if (linkMenuOpen) {
    return (
      <CanvasAddLinkPopover
        onClose={() => setLinkMenuOpen(false)}
        onSubmit={(url) => {
          props.onAddLinkSubmit(url);
          onCloseMenu();
        }}
      />
    );
  }

  if (view.kind === "integration") {
    const connection = buildConnectionForSelector(view.integration);
    if (!connection) return null;

    return (
      <div
        className="flex flex-col overflow-hidden rounded-lg border border-n-10 bg-n-00 shadow-elev-5"
        style={{
          width: INSERT_MENU_INTEGRATION_WIDTH,
          height: INSERT_MENU_INTEGRATION_HEIGHT,
        }}
      >
        <CanvasInsertMenuExpandedHeader
          icon={
            <IntegrationIcon
              platform={view.integration.platform}
              name={view.integration.name}
              imageURL={view.integration.metadata?.image_url}
              className="size-4 rounded"
            />
          }
          label={view.integration.name}
          onBack={() => setView({ kind: "menu" })}
        />
        <div className="min-h-0 flex-1">
          <EntitySelectionProvider key={connection.id}>
            <EntitySelector
              key={connection.id}
              connection={connection}
              onClose={onCloseMenu}
              onSelect={handleIntegrationSelect}
              className="flex-1 border-0"
            />
          </EntitySelectionProvider>
        </div>
      </div>
    );
  }

  return (
    <>
      {props.useInternalFileInput && (
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          accept={CANVAS_FILE_UPLOAD_ACCEPT}
        />
      )}
      <CanvasInsertMenuItems
        onOpenWidgets={() => {
          setLinkMenuOpen(false);
          setView({ kind: "menu" });
          setWidgetsMenuOpen(true);
        }}
        onOpenLink={() => {
          setWidgetsMenuOpen(false);
          setView({ kind: "menu" });
          setLinkMenuOpen(true);
        }}
        onOpenIntegration={(integration) => {
          setWidgetsMenuOpen(false);
          setLinkMenuOpen(false);
          setView({ kind: "integration", integration });
        }}
        connectedIntegrations={connectedIntegrations}
        integrationsPending={integrationsPending}
        onNewNote={props.onNewNote}
        onNewDocument={props.onNewDocument}
        onUploadFile={handleUploadSelect}
        onUploadFolder={props.onUploadFolder}
        onAddFromLibrary={props.onAddFromLibrary}
        onAddCanvas={props.onAddCanvas}
        preventCloseOnUploadSelect={props.useInternalFileInput}
        onStartQuickChat={props.onStartQuickChat}
        onStartQuickVoice={props.onStartQuickVoice}
        onStartComment={props.onStartComment}
        onPaste={props.onPaste}
      />
    </>
  );
}

export function CanvasInsertMenu(props: CanvasInsertMenuProps) {
  const isToolbar = props.variant === "toolbar";
  const anchoredProps = isToolbar ? null : props;
  const pickingFileRef = useRef(false);

  const menuState = useInsertMenuState(
    props,
    anchoredProps?.open,
    anchoredProps?.onOpenChange,
  );

  const toolbarLayout = useCanvasToolbarLayoutOptional();

  const railAnchorRect = useElementAnchorRect(
    !!anchoredProps?.anchor.anchorToRailTrigger && !!anchoredProps.open,
    CANVAS_ADD_CONTEXT_TRIGGER_SELECTOR,
  );

  const closeMenu = useCallback(() => {
    menuState.handleOpenChange(false);
  }, [menuState]);

  const body = (
    <CanvasInsertMenuBody
      props={props}
      menuState={menuState}
      onCloseMenu={closeMenu}
      pickingFileRef={pickingFileRef}
    />
  );

  const contentClassName =
    menuState.replacementViewOpen && menuState.view.kind !== "integration"
      ? "border-0 bg-transparent p-0 shadow-none"
      : menuState.view.kind === "integration"
        ? "border-0 bg-transparent p-0 shadow-none"
        : undefined;

  if (isToolbar) {
    if (!toolbarLayout) {
      throw new Error(
        "CanvasInsertMenu toolbar variant requires CanvasToolbarLayoutProvider",
      );
    }
    const {
      tooltipSide: layoutTooltipSide,
      menuSide,
      submenuGapPx,
    } = toolbarLayout;

    return (
      <CanvasToolbarDropdown
        label="Insert"
        tooltipSide={props.tooltipSide ?? layoutTooltipSide}
        icon={<Plus className="size-4" strokeWidth={2} />}
        showCaret={false}
        side={menuSide}
        align="center"
        sideOffset={submenuGapPx}
        minWidth={menuState.minWidth}
        contentClassName={contentClassName}
        onOpen={props.onOpen}
        open={menuState.insertMenuOpen || menuState.replacementViewOpen}
        onOpenChange={menuState.handleOpenChange}
      >
        {body}
      </CanvasToolbarDropdown>
    );
  }

  const { cursorPosition, anchorToRailTrigger } = anchoredProps!.anchor;
  const menuOpen = menuState.insertMenuOpen || menuState.replacementViewOpen;

  const anchorStyle =
    anchorToRailTrigger && railAnchorRect
      ? {
          position: "fixed" as const,
          left: railAnchorRect.left,
          top: railAnchorRect.top,
          width: railAnchorRect.width,
          height: railAnchorRect.height,
          pointerEvents: "none" as const,
        }
      : {
          position: "fixed" as const,
          left: cursorPosition.clientX,
          top: cursorPosition.clientY,
          width: 0,
          height: 0,
          pointerEvents: "none" as const,
        };

  return (
    <DropdownMenu
      open={menuOpen}
      onOpenChange={menuState.handleOpenChange}
      modal={false}
    >
      <DropdownMenuTrigger asChild>
        <span aria-hidden style={anchorStyle} />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side={anchorToRailTrigger ? "right" : "bottom"}
        align="start"
        sideOffset={4}
        collisionPadding={VIEWPORT_POPOVER_COLLISION_PADDING}
        collisionBoundary={getViewportDocumentCollisionBoundary()}
        className={canvasToolbarDropdownContentClassName}
        style={{
          minWidth: menuState.minWidth,
          ...(menuState.view.kind === "integration"
            ? {
                width: INSERT_MENU_INTEGRATION_WIDTH,
                height: INSERT_MENU_INTEGRATION_HEIGHT,
                padding: 0,
              }
            : {}),
        }}
        onPointerDownOutside={(e) => {
          if (pickingFileRef.current) {
            e.preventDefault();
            return;
          }
          const target = e.target as HTMLElement;
          if (target.closest?.("[data-canvas-add-context-trigger]")) {
            e.preventDefault();
          }
        }}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        {body}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
